/**
 * MPQ hashing and block cipher. The 1280-entry table is generated, never stored.
 */
const cryptTable = (() => {
  const t = new Uint32Array(0x500);
  let seed = 0x00100001;
  for (let i1 = 0; i1 < 0x100; i1++) {
    let i2 = i1;
    for (let i = 0; i < 5; i++, i2 += 0x100) {
      seed = (seed * 125 + 3) % 0x2aaaab;
      const hi = (seed & 0xffff) << 16;
      seed = (seed * 125 + 3) % 0x2aaaab;
      const lo = seed & 0xffff;
      t[i2] = (hi | lo) >>> 0;
    }
  }
  return t;
})();

export function cryptTableEntry(i: number): number {
  return cryptTable[i];
}

export const enum HashType {
  TableOffset = 0,
  NameA = 1,
  NameB = 2,
  FileKey = 3,
}

/** Normalizes an archive path the way Storm does: ASCII upper-case, `/` → `\`. */
export function normalizePath(name: string): string {
  return name.replace(/\//g, '\\').toUpperCase();
}

export function hashString(name: string, type: HashType): number {
  let seed1 = 0x7fed7fed;
  let seed2 = 0xeeeeeeee;
  const s = normalizePath(name);
  for (let i = 0; i < s.length; i++) {
    const ch = s.charCodeAt(i) & 0xff;
    seed1 = (cryptTable[(type << 8) + ch] ^ (seed1 + seed2)) >>> 0;
    seed2 = (ch + seed1 + seed2 + (seed2 << 5) + 3) >>> 0;
  }
  return seed1 >>> 0;
}

/** Decrypts `words` in place. */
export function decryptBlock(words: Uint32Array, key: number): void {
  let seed = 0xeeeeeeee;
  let k = key >>> 0;
  for (let i = 0; i < words.length; i++) {
    seed = (seed + cryptTable[0x400 + (k & 0xff)]) >>> 0;
    const ch = (words[i] ^ (k + seed)) >>> 0;
    k = (((~k << 21) + 0x11111111) | (k >>> 11)) >>> 0;
    seed = (ch + seed + (seed << 5) + 3) >>> 0;
    words[i] = ch;
  }
}

/** Encrypts `words` in place (used by our test-fixture writer). */
export function encryptBlock(words: Uint32Array, key: number): void {
  let seed = 0xeeeeeeee;
  let k = key >>> 0;
  for (let i = 0; i < words.length; i++) {
    seed = (seed + cryptTable[0x400 + (k & 0xff)]) >>> 0;
    const ch = words[i];
    words[i] = (ch ^ (k + seed)) >>> 0;
    k = (((~k << 21) + 0x11111111) | (k >>> 11)) >>> 0;
    seed = (ch + seed + (seed << 5) + 3) >>> 0;
  }
}

/** Decrypts a byte buffer whose length may not be a multiple of 4 (trailing bytes stay plain). */
export function decryptBytes(bytes: Uint8Array, key: number): Uint8Array {
  const n = bytes.length >>> 2;
  if (n === 0) return bytes;
  const copy = new Uint8Array(bytes);
  const words = new Uint32Array(copy.buffer, 0, n);
  decryptBlock(words, key);
  return copy;
}

export function encryptBytes(bytes: Uint8Array, key: number): Uint8Array {
  const n = bytes.length >>> 2;
  const copy = new Uint8Array(bytes);
  if (n === 0) return copy;
  encryptBlock(new Uint32Array(copy.buffer, 0, n), key);
  return copy;
}

export function fileKey(path: string, blockOffset: number, fileSize: number, fixKey: boolean): number {
  const base = path.split(/[\\/]/).pop() ?? path;
  let key = hashString(base, HashType.FileKey);
  if (fixKey) key = ((key + blockOffset) ^ fileSize) >>> 0;
  return key;
}
